/**
 * Where a world is kept, without saying where that is.
 *
 * The server writes worlds into a directory with `fs`. A browser cannot, and
 * a browser is about to be asked to hold a world - "я хочу перекинути
 * навантаження на гравця який створює світ". So everything that knows *how*
 * a world is written down now goes through one small interface, and the two
 * places that can actually write provide it: files on the server, and the
 * browser's own store on a player's machine.
 *
 * Reads are synchronous and writes may be deferred, which is the shape that
 * suits both. A browser's store is asynchronous, so a browser's `Keep` reads
 * everything it has once, up front, before any world is grown - and after
 * that answers out of memory, which is where the world lives anyway.
 *
 * What travels through here is bytes and text, never objects: the encoding
 * of a world is `core/save/worldFile.ts`'s business and is already shared.
 */

import { World } from "../world/world.js";
import {
  encodeWorld, decodeWorld, sameWorld, type SavedChunk,
} from "../save/worldFile.js";

export interface Keep {
  /** What was written under this name, or null if nothing ever was. */
  read(name: string): Uint8Array | null;
  /** Write it. May land later; must not throw for a name nobody has read. */
  write(name: string, bytes: Uint8Array): void;
  /** Throw the lot away: a match that is over, a world somebody deleted. */
  forget(): void;
}

const WORLD = "world.bin";
const BAGS = "bags.json";
const NAMES = "names.json";
const BOATS = "boats.json";
const META = "meta.json";

/**
 * Everything a world is made of, by name.
 *
 * Exported because a `Keep` that has to fetch its contents before it can
 * answer synchronously - a browser one, where the storage is asynchronous
 * and the game is not - needs to know what to go and fetch. Anything added
 * here and left out of this list would silently stop surviving a reload,
 * which is the kind of fault that is only noticed weeks later by somebody
 * who lost an evening.
 */
export const KEPT_FILES: readonly string[] = [WORLD, BAGS, NAMES, BOATS, META];

const text = new TextDecoder();
const bytes = new TextEncoder();

function readJson<T>(keep: Keep, name: string, fallback: T): T {
  const raw = keep.read(name);
  if (raw === null) return fallback;
  try {
    const parsed: unknown = JSON.parse(text.decode(raw));
    return parsed === null || typeof parsed !== "object"
      ? fallback : parsed as T;
  } catch {
    /*
     * Unreadable is not empty, and must not be treated as empty.
     *
     * The same rule the accounts file lives by: what is there stays there,
     * and whoever is looking at the logs finds out. Writing over it is how a
     * world is lost.
     */
    return fallback;
  }
}

function writeJson(keep: Keep, name: string, value: unknown): void {
  keep.write(name, bytes.encode(JSON.stringify(value)));
}

export type LoadResult =
  | { readonly kind: "loaded"; readonly chunks: number }
  | { readonly kind: "fresh" }
  | { readonly kind: "refused"; readonly why: string };

/** Read the world out of the keep into `world`, or say why not. */
export function loadWorldFrom(world: World, keep: Keep): LoadResult {
  const raw = keep.read(WORLD);
  if (raw === null) return { kind: "fresh" };

  let read;
  try {
    read = decodeWorld(raw.buffer.slice(
      raw.byteOffset, raw.byteOffset + raw.byteLength) as ArrayBuffer);
  } catch (error) {
    return { kind: "refused", why: `could not be read: ${String(error)}` };
  }
  if (!read.ok) return { kind: "refused", why: `unreadable (${read.why})` };
  if (!sameWorld(read.world, world.opts)) {
    return {
      kind: "refused",
      why: `it is a different world - saved ${read.world.seed}/` +
        `${read.world.sizeXZ}x${read.world.sizeY}, this one is ` +
        `${world.opts.seed}/${world.opts.sizeXZ}x${world.opts.sizeY}`,
    };
  }
  for (const chunk of read.world.chunks) {
    world.loadChunk(chunk.cx, chunk.cy, chunk.cz, chunk.blocks);
  }
  return { kind: "loaded", chunks: read.world.chunks.length };
}

/** Write the world into the keep. Whole, or not at all. */
export function saveWorldTo(world: World, keep: Keep): number {
  const chunks: SavedChunk[] = world.modifiedChunks().map((c) => ({
    cx: c.cx, cy: c.cy, cz: c.cz, blocks: c.blocks,
  }));
  keep.write(WORLD, new Uint8Array(encodeWorld({
    seed: world.opts.seed,
    sizeXZ: world.opts.sizeXZ,
    sizeY: world.opts.sizeY,
    kremlin: world.opts.kremlin !== false,
    chunks,
  })));
  return chunks.length;
}

/** Everybody's bag in this world, by name. */
export function loadBagsFrom(keep: Keep): Map<string, unknown> {
  return new Map(Object.entries(
    readJson<Record<string, unknown>>(keep, BAGS, {})));
}

export function saveBagsTo(keep: Keep, bags: Map<string, unknown>): void {
  writeJson(keep, BAGS, Object.fromEntries(bags));
}

/** Who owns which name here. */
export function loadNamesFrom(keep: Keep): Map<string, string> {
  return new Map(Object.entries(readJson<Record<string, string>>(keep, NAMES, {})));
}

export function saveNamesTo(keep: Keep, names: Map<string, string>): void {
  writeJson(keep, NAMES, Object.fromEntries(names));
}

export interface KeptBoat { x: number; y: number; z: number; yaw: number }

export function loadBoatsFrom(keep: Keep): KeptBoat[] {
  const kept = readJson<KeptBoat[]>(keep, BOATS, []);
  return Array.isArray(kept) ? kept.filter((b) =>
    typeof b?.x === "number" && typeof b?.y === "number" &&
    typeof b?.z === "number" && typeof b?.yaw === "number") : [];
}

export function saveBoatsTo(keep: Keep, boats: readonly KeptBoat[]): void {
  writeJson(keep, BOATS, boats);
}

/** A world's own small facts: where it has got to in «Один блок»'s table. */
/**
 * The small things about a world that are not the world.
 *
 * What it is, rather than what is in it: which map, what seed its country
 * grew from, and the word its door asks for. Kept *with* the world rather
 * than beside it, because the thing most likely to go wrong when a world
 * comes back is opening it as a different one - `sameWorld` refuses that,
 * loudly, and this is what stops it happening in the first place.
 *
 * All optional. A world written before any of this existed has none of it,
 * and a `Realm` that never opened a шопопало-блок has no count either.
 */
export interface WorldMeta {
  oneBlockBreaks?: number;
  readonly map?: string;
  readonly seed?: number;
  /**
   * The word its door asks for.
   *
   * Kept so that coming back keeps the same door: the point of a world
   * surviving at all is that the people who were in it can return, and they
   * return with the word they were given. A different word each evening is
   * the same as the world not coming back.
   */
  readonly word?: string;
  /** When it was last written, so a screen can say when it was last played. */
  savedAt?: number;
}

export function loadMetaFrom(keep: Keep): WorldMeta {
  return readJson<WorldMeta>(keep, META, {});
}

/**
 * Write it, keeping whatever else was already in there.
 *
 * A `Realm` records one number here - how many times a шопопало-блок has
 * been hit - and knows nothing about which map it is or what word opens it.
 * Writing only what the caller passed would quietly throw the rest away,
 * and the world would come back as a different one the next evening.
 */
export function saveMetaTo(keep: Keep, meta: WorldMeta): void {
  writeJson(keep, META, { ...loadMetaFrom(keep), ...meta });
}

/**
 * A keep that holds everything in memory and nothing anywhere else.
 *
 * For a game nobody wants written down - a match that ends and is gone - and
 * for tests, which is most of why it is here: every rule above can be tried
 * without a disk, a browser or a temporary directory.
 */
export function memoryKeep(): Keep {
  const held = new Map<string, Uint8Array>();
  return {
    read: (name) => held.get(name) ?? null,
    write: (name, value) => { held.set(name, value); },
    forget: () => { held.clear(); },
  };
}
